import { useEffect, useRef } from 'react';
import * as assistantService from 'services/assistant';
import {
  DynamicProposal,
  FlProposal,
  HotProposal,
  ILPacProposal,
  Proposal,
  TransferProposal,
  TripBuilderConfig,
} from 'ts/shared/interfaces/CreateProposalParams';
import { ClaudeHotelInfoConfig } from 'utils/shared/claudeProposalInformation';
import { HotelInfoConfig } from 'utils/shared/hotelProposalInformation';

export type ProposalActionPayload = assistantService.ProposalActionPayload;

const ACTION_ALIASES: Record<string, assistantService.ProposalActionKind> = {
  tab: 'page-tab',
  page: 'page-tab',
  delta: 'cancellation-delta',
  'cancellation-date-delta': 'cancellation-delta',
  'cxl-delta': 'cancellation-delta',
  cancellation: 'show-cancellation-date',
  'show-cancellation': 'show-cancellation-date',
  cxl: 'show-cancellation-date',
  price: 'show-price',
  'display-price': 'show-price',
  'hide-price': 'show-price',
  'hide-prices': 'show-price',
  clear: 'clear-all',
  reset: 'clear-all',
  'clear all': 'clear-all',
  clearall: 'clear-all',
  'create-proposal': 'create',
  'create proposal': 'create',
  'save-proposal': 'save',
  'save proposal': 'save',
  update: 'save',
  note: 'comment',
  notes: 'comment',
  'edit-notes': 'comment',
  'edit note': 'comment',
  'edit-note': 'comment',
  'add-notes': 'comment',
  'add note': 'comment',
  'add-note': 'comment',
  'append-notes': 'comment',
  'append note': 'comment',
  lang: 'language',
  localization: 'language',
  'set-language': 'language',
  'set language': 'language',
  'set-template': 'template',
  'set template': 'template',
  'set-sort': 'sort',
  'set sort': 'sort',
  'set-currency': 'currency',
  'set currency': 'currency',
  delete: 'remove',
  trash: 'remove',
  'remove-item': 'remove',
  'remove-proposal': 'remove',
  edit: 'edit',
  'edit-proposal': 'edit',
  'edit-item': 'edit',
  'open-edit': 'edit',
  pencil: 'edit',
  'notes-modal': 'edit',
  room: 'select-room',
  'toggle-room': 'select-room',
  rooms: 'show-rooms',
  'expand-rooms': 'show-rooms',
  filter: 'filter-rooms',
  'room-filter': 'filter-rooms',
  'filter-room': 'filter-rooms',
  'rooms-filter': 'filter-rooms',
  'open-tab': 'hotel-tab',
  'price-edit': 'edit-price',
  'edit price': 'edit-price',
  'edit-prices': 'edit-price',
  'open-price': 'edit-price',
  'open-edit-price': 'edit-price',
  'pencil-price': 'edit-price',
  'price-pencil': 'edit-price',
  markup: 'edit-price',
  'add-percent': 'edit-price',
  'add-percentage': 'edit-price',
  freetext: 'free-text',
  'notes-text': 'free-text',
  print: 'share',
  download: 'share',
  email: 'share',
  whatsapp: 'share',
  telegram: 'share',
  send: 'share',
  'send-email': 'share',
  'send email': 'share',
  'email-proposal': 'share',
  'send-proposal': 'share',
  'send proposal': 'share',
  check: 'source-check',
  checkbox: 'source-check',
  'add-to': 'source-check',
  'add-to-proposal': 'source-check',
  tick: 'source-check',
  uncheck: 'source-check',
  untick: 'source-check',
  load: 'load-source',
};

const PRODUCT_ALIASES: Record<string, assistantService.ProposalActionProduct> = {
  hotel: 'hotels',
  hotels: 'hotels',
  flight: 'flights',
  flights: 'flights',
  dynamic: 'dynamic',
  dyn: 'dynamic',
  transfer: 'transfers',
  transfers: 'transfers',
  package: 'packages',
  packages: 'packages',
  il: 'packages',
  ilpackages: 'packages',
};

const PAGE_TABS: Record<string, 'search' | 'items' | 'trip'> = {
  search: 'search',
  items: 'items',
  item: 'items',
  'item-view': 'items',
  trip: 'trip',
  'trip-view': 'trip',
  builder: 'trip',
};

const HOTEL_TABS: Record<string, number> = {
  rooms: 0,
  room: 0,
  info: 1,
  information: 1,
  tripadvisor: 2,
  ta: 2,
  www: 3,
  google: 3,
  ai: 4,
  claude: 4,
};

const PRICE_TYPES: Record<string, 'p' | 'v' | 'e'> = {
  v: 'v',
  value: 'v',
  fixed: 'v',
  'fixed value': 'v',
  'fixed-value': 'v',
  'fixed_value': 'v',
  p: 'p',
  percent: 'p',
  percentage: 'p',
  '%': 'p',
  e: 'e',
  exact: 'e',
  overwrite: 'e',
  'overwrite price': 'e',
  'overwrite-price': 'e',
  'set price': 'e',
  'set-price': 'e',
};

const CHANNELS: Record<string, 'email' | 'pdf' | 'whatsapp' | 'telegram'> = {
  email: 'email',
  mail: 'email',
  pdf: 'pdf',
  print: 'pdf',
  download: 'pdf',
  whatsapp: 'whatsapp',
  wa: 'whatsapp',
  telegram: 'telegram',
  tg: 'telegram',
};

const SOURCES: Record<string, 'google' | 'tripadvisor' | 'info' | 'ai'> = {
  google: 'google',
  www: 'google',
  tripadvisor: 'tripadvisor',
  ta: 'tripadvisor',
  info: 'info',
  'hotel-info': 'info',
  information: 'info',
  ai: 'ai',
  claude: 'ai',
};

const SOURCE_FROM_TAB: Record<string, 'google' | 'tripadvisor' | 'info' | 'ai'> = {
  info: 'info',
  information: 'info',
  'hotel-info': 'info',
  www: 'google',
  google: 'google',
  tripadvisor: 'tripadvisor',
  ta: 'tripadvisor',
  ai: 'ai',
  claude: 'ai',
};

const normalizeCheckboxField = (
  field: string,
): ProposalActionPayload['field'] => {
  const key = field.trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (
    key === 'details' ||
    key === 'detail' ||
    key === 'hotel details' ||
    key === 'hotel detail'
  ) {
    return 'details';
  }
  if (key === 'overview' || key === 'hotel overview') {
    return 'overview';
  }
  if (key === 'reviews' || key === 'review') {
    return 'reviews';
  }
  if (key === 'amenities' || key === 'amenity') {
    return 'amenities';
  }
  if (
    key === 'proscons' ||
    key === 'pros' ||
    key === 'pros cons' ||
    key === 'pros and cons'
  ) {
    return 'prosCons';
  }
  if (key === 'images' || key === 'image' || key === 'photos' || key === 'photo') {
    return 'images';
  }
  return null;
};

const sourceFromField = (
  field: NonNullable<ProposalActionPayload['field']>,
): 'google' | 'tripadvisor' | 'info' | 'ai' => {
  if (field === 'prosCons') {
    return 'ai';
  }
  if (field === 'amenities') {
    return 'tripadvisor';
  }
  if (field === 'reviews') {
    return 'google';
  }
  return 'info';
};

const CLAUDE_LANGUAGE: Record<string, string> = {
  en: 'English',
  he: 'Hebrew',
  es: 'Spanish',
  it: 'Italian',
  fr: 'French',
};

const ORDINAL_WORDS: Record<string, number> = {
  first: 1,
  '1st': 1,
  second: 2,
  '2nd': 2,
  third: 3,
  '3rd': 3,
  fourth: 4,
  '4th': 4,
  fifth: 5,
  '5th': 5,
};

const asString = (value: unknown) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
};

const asBool = (value: unknown) => (typeof value === 'boolean' ? value : null);

const asNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const normalizeLanguage = (raw: string | null) => {
  if (!raw) {
    return null;
  }
  const key = raw.trim().toLowerCase();
  if (['en', 'eng', 'english'].includes(key)) return 'en';
  if (['he', 'heb', 'hebrew', 'עברית', 'ivrit'].includes(key)) return 'he';
  if (['es', 'spa', 'spanish', 'español', 'espanol'].includes(key)) return 'es';
  if (['it', 'ita', 'italian', 'italiano'].includes(key)) return 'it';
  if (['fr', 'fra', 'fre', 'french', 'français', 'francais'].includes(key)) return 'fr';
  if (['ru', 'rus', 'russian', 'русский', 'russkiy'].includes(key)) return 'ru';
  return null;
};

const normalizeTemplate = (raw: string | null) => {
  if (!raw) {
    return null;
  }
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (
    ['t1', '1', 'first', '1st', 'simple', 'simple template', 'simple_template'].includes(key)
  ) {
    return 't1';
  }
  if (
    [
      't2',
      '2',
      'second',
      '2nd',
      'colorful',
      'colorful template',
      'colorful_template',
    ].includes(key)
  ) {
    return 't2';
  }
  if (
    [
      't3',
      '3',
      'third',
      '3rd',
      'contrast',
      'contrast template',
      'contrast_template',
    ].includes(key)
  ) {
    return 't3';
  }
  return null;
};

const normalizeSort = (raw: string | null) => {
  if (!raw) {
    return null;
  }
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (
    ['1', 'order', 'by order', 'by order #', 'by order id', 'order id', 'sort by order'].includes(
      key,
    )
  ) {
    return '1';
  }
  if (
    [
      '2',
      'date',
      'service date',
      'by service date',
      'by date',
      'service',
      'sort by date',
      'sort by service date',
      'service-date',
    ].includes(key)
  ) {
    return '2';
  }
  if (
    ['3', 'original', 'by original', 'original order', 'original sort', 'מקורי'].includes(key)
  ) {
    return '3';
  }
  if (/\b(service\s*)?date\b/.test(key)) {
    return '2';
  }
  if (/\border\b/.test(key)) {
    return '1';
  }
  return null;
};

const normalizeCurrency = (raw: string | null) => {
  if (!raw) {
    return null;
  }
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (['usd', 'dollar', 'dollars', '$', 'us$', 'us dollar'].includes(key)) return 'usd';
  if (['eur', 'euro', 'euros', '€'].includes(key)) return 'eur';
  if (['gbp', 'pound', 'pounds', '£', 'sterling'].includes(key)) return 'gbp';
  if (['thb', 'baht', '฿'].includes(key)) return 'thb';
  return null;
};

const resolveTargetRef = (hotelName: string | null, index: number | null) => {
  let nextIndex = index;
  let nextName = hotelName;

  if (nextName) {
    const hash = nextName.match(/^#?(\d+)$/i);
    if (hash) {
      nextIndex = Number(hash[1]);
      nextName = null;
    } else if (ORDINAL_WORDS[nextName.toLowerCase()]) {
      nextIndex = ORDINAL_WORDS[nextName.toLowerCase()];
      nextName = null;
    }
  }

  return { hotelName: nextName, index: nextIndex };
};

const parseRoomIndexList = (raw: unknown): number[] | null => {
  if (Array.isArray(raw)) {
    const indexes = raw
      .map((item) => {
        if (typeof item === 'number' && Number.isFinite(item)) {
          return Math.floor(item);
        }
        if (typeof item === 'string' && /^\s*#?\d+\s*$/.test(item)) {
          return Number(item.trim().replace(/^#/, ''));
        }
        return null;
      })
      .filter((item): item is number => item != null && item >= 1);
    return indexes.length ? [...new Set(indexes)] : null;
  }
  if (typeof raw === 'string') {
    return parseRoomIndexesFromText(raw);
  }
  return null;
};

const parseRoomIndexesFromText = (text: string): number[] | null => {
  const value = text.trim();
  if (!value) {
    return null;
  }

  const range = value.match(/^#?(\d+)\s*(?:-|–|—|\s+to\s+)\s*#?(\d+)$/i);
  if (range) {
    const from = Number(range[1]);
    const to = Number(range[2]);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < 1) {
      return null;
    }
    const start = Math.min(from, to);
    const end = Math.max(from, to);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  const parts = value.split(/[,\s]+/).filter(Boolean);
  if (parts.length > 1 && parts.every((part) => /^#?\d+$/.test(part))) {
    return [...new Set(parts.map((part) => Number(part.replace(/^#/, ''))).filter((n) => n >= 1))];
  }

  return null;
};

export type ProposalItemRef = {
  name: string;
  product: assistantService.ProposalActionProduct;
  index: number;
};

let proposalItemsForAssistant: ProposalItemRef[] = [];

export const setProposalItemsForAssistant = (items: ProposalItemRef[]) => {
  proposalItemsForAssistant = items;
};

export const getProposalItemsForAssistant = () => proposalItemsForAssistant;

export const resolveProposalOrdinalsInText = (text: string, items: ProposalItemRef[]): string => {
  if (!text || !items.length) {
    return text;
  }

  const hotels = items.filter((item) => item.product === 'hotels');
  const pool = hotels.length ? hotels : items;

  let result = text.replace(/#(\d+)\b/g, (match, raw) => {
    const item = pool[Number(raw) - 1];
    return item?.name || match;
  });

  result = result.replace(
    /\b(first|1st|second|2nd|third|3rd|fourth|4th|fifth|5th)(?:\s+(?:hotel|item|proposal))?\b/gi,
    (match, word: string) => {
      const item = pool[ORDINAL_WORDS[word.toLowerCase()] - 1];
      return item?.name || match;
    },
  );

  return result;
};

const joinProposalNotes = (current: string, addition: string, mode?: 'replace' | 'append' | null) => {
  if (mode !== 'append') {
    return addition;
  }
  const cur = current.trim();
  const add = addition.trim();
  if (!cur) {
    return add;
  }
  if (!add) {
    return cur;
  }
  return `${cur} ${add}`;
};

const unquoteNoteText = (value: string) =>
  value
    .trim()
    .replace(/^["'`«]+|["'`»]+$/g, '')
    .trim();

const resolveBareComment = (lower: string): ProposalActionPayload | null => {
  if (/\bcreate\b/.test(lower) && /\bproposal\b/.test(lower)) {
    return null;
  }
  if (/\b(?:free\s*text|freetext|item\s+notes?|hotel\s+notes?)\b/.test(lower)) {
    return null;
  }

  const appendMatch =
    lower.match(
      /\b(?:add|append)\s+(?:to\s+)?(?:the\s+)?(?:notes?|comments?)\s*(?:with\s+|:\s*)?(.+)$/i,
    ) ||
    lower.match(/\b(?:add|append)\s+(.+?)\s+to\s+(?:the\s+)?(?:notes?|comments?)\b/i);
  if (appendMatch?.[1]) {
    const text = unquoteNoteText(appendMatch[1]);
    if (text) {
      return { action: 'comment', text, mode: 'append' };
    }
  }

  const replaceMatch =
    lower.match(
      /\b(?:edit|change|set|replace|update)\s+(?:the\s+)?(?:notes?|comments?)\s+(?:to|with|as)\s+(.+)$/i,
    ) ||
    lower.match(/\b(?:notes?|comments?)\s*(?:to|=|:)\s+(.+)$/i) ||
    lower.match(/\b(?:write|put)\s+(?:in\s+)?(?:the\s+)?(?:notes?|comments?)\s+(.+)$/i);
  if (replaceMatch?.[1]) {
    const text = unquoteNoteText(replaceMatch[1]);
    if (text) {
      return { action: 'comment', text, mode: 'replace' };
    }
  }

  return null;
};

const resolveBareShare = (lower: string): ProposalActionPayload | null => {
  if (/\bcreate\b/.test(lower) && /\bproposal\b/.test(lower)) {
    return null;
  }

  const emailFromText = lower.match(/\b([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})\b/i)?.[1];

  if (
    /\b(?:send|share)\b.{0,30}\b(?:by\s+|via\s+)?(?:e-?mail)\b/.test(lower) ||
    /\b(?:e-?mail|mail)\s+(?:the\s+)?proposal\b/.test(lower) ||
    /\bsend(?:-|\s+)e-?mail\b/.test(lower) ||
    /^(?:please\s+)?(?:send|e-?mail)(?:\s+it)?[.!?]?$/.test(lower)
  ) {
    return {
      action: 'share',
      channel: 'email',
      ...(emailFromText ? { email: emailFromText } : {}),
    };
  }

  if (/\b(?:send|share)\b.{0,30}\bwhatsapp\b/.test(lower) || /\bwhatsapp\b/.test(lower)) {
    if (/\b(?:send|share)\b/.test(lower) || /^(?:please\s+)?whatsapp[.!?]?$/.test(lower)) {
      return { action: 'share', channel: 'whatsapp' };
    }
  }

  if (/\b(?:send|share)\b.{0,30}\btelegram\b/.test(lower) || /\btelegram\b/.test(lower)) {
    if (/\b(?:send|share)\b/.test(lower) || /^(?:please\s+)?telegram[.!?]?$/.test(lower)) {
      return { action: 'share', channel: 'telegram' };
    }
  }

  if (
    /\b(?:download|print)\b.{0,20}\b(?:pdf|proposal)\b/.test(lower) ||
    /\b(?:pdf|download)\s+(?:the\s+)?proposal\b/.test(lower) ||
    /^(?:please\s+)?(?:download|print|pdf)[.!?]?$/.test(lower)
  ) {
    return { action: 'share', channel: 'pdf' };
  }

  return null;
};

const resolveBareCreateProposal = (lower: string): ProposalActionPayload | null => {
  if (
    !/\b(?:create|save)\s+(?:a\s+)?(?:the\s+)?proposal\b/.test(lower) &&
    !/\bcreate\s+proposal\b/.test(lower) &&
    !/\bproposal\s+for\b/.test(lower)
  ) {
    return null;
  }

  const nameStop =
    'email|password|expire|cxl|cancellation|show|hide|display|delta|with|without|and|enable|disable';
  const name =
    lower.match(
      new RegExp(
        String.raw`\b(?:for|name|owner)\s+([a-z][a-z\s.'-]{1,40}?)(?=\s+(?:${nameStop})(?:\b|$)|$)`,
        'i',
      ),
    )?.[1] ||
    lower.match(
      new RegExp(
        String.raw`\bcreate\s+(?:a\s+)?proposal\s+(?:named\s+)?([a-z][a-z\s.'-]{1,40}?)(?=\s+(?:${nameStop})(?:\b|$)|$)`,
        'i',
      ),
    )?.[1] ||
    null;
  const ownerName = name?.trim().replace(/\s+/g, ' ');
  if (!ownerName || ownerName.length < 2) {
    return null;
  }

  const email = lower.match(/\bemail\s*[:=]?\s*([^\s]+@[^\s]+)/)?.[1] || null;
  const password = lower.match(/\bpassword\s*[:=]?\s*([^\s]+)/)?.[1] || null;
  const expireDate =
    lower.match(/\bexpire(?:\s+date)?\s*[:=]?\s*(\d{4}-\d{2}-\d{2})\b/)?.[1] || null;
  const deltaRaw =
    lower.match(/\b(?:cancellation(?:\s+date)?\s+delta|cxl\s*delta|delta)\s*[:=]?\s*([0-5])\b/)?.[1] ||
    null;
  const cancellationDateDelta = deltaRaw != null ? Number(deltaRaw) : null;

  let showCancellationDate: boolean | null = null;
  if (/\b(?:show|with)\s+cancellation(?:\s+date)?\b/.test(lower)) showCancellationDate = true;
  if (/\b(?:hide|without)\s+cancellation(?:\s+date)?\b/.test(lower)) showCancellationDate = false;

  let displayTotalDelta: boolean | null = null;
  if (/\b(?:show|with|enable)\s+display\s+total\b/.test(lower) || /\bdisplay\s+total\b/.test(lower)) {
    displayTotalDelta = !/\b(?:hide|without|disable)\s+display\s+total\b/.test(lower);
  }
  if (/\b(?:hide|without|disable)\s+display\s+total\b/.test(lower)) displayTotalDelta = false;

  return {
    action: 'create',
    ownerName,
    ...(email ? { email } : {}),
    ...(password ? { password } : {}),
    ...(expireDate ? { expireDate } : {}),
    ...(cancellationDateDelta != null ? { cancellationDateDelta } : {}),
    ...(typeof showCancellationDate === 'boolean' ? { showCancellationDate } : {}),
    ...(typeof displayTotalDelta === 'boolean' ? { displayTotalDelta } : {}),
  };
};

const bareProposalTarget = (lower: string) => {
  const hash = lower.match(/#\s*(\d+)\b/);
  if (hash) {
    return { index: Number(hash[1]), hotelName: null as string | null };
  }
  const ordinal = lower.match(/\b(first|1st|second|2nd|third|3rd|fourth|4th|fifth|5th)\b/);
  if (ordinal) {
    return { index: ORDINAL_WORDS[ordinal[1]], hotelName: null as string | null };
  }
  return { index: null as number | null, hotelName: null as string | null };
};

const bareProposalSource = (
  lower: string,
  hints: { reviews?: boolean; amenities?: boolean; prosCons?: boolean },
): 'google' | 'tripadvisor' | 'info' | 'ai' => {
  if (/\b(?:google|www)\b/.test(lower)) return 'google';
  if (/\b(?:tripadvisor|trip\s*advisor|ta)\b/.test(lower)) return 'tripadvisor';
  if (/\b(?:ai|claude)\b/.test(lower)) return 'ai';
  if (/\b(?:hotel\s+)?info\b/.test(lower)) return 'info';
  if (hints.reviews) return 'google';
  if (hints.amenities) return 'tripadvisor';
  if (hints.prosCons) return 'ai';
  return 'info';
};

const resolveBareSourceChecks = (lower: string): ProposalActionPayload[] => {
  const wantsDetails = /\b(?:hotel\s+)?details?\b/.test(lower);
  const wantsOverview = /\boverview\b/.test(lower);
  const wantsReviews = /\breviews?\b/.test(lower);
  const wantsAmenities = /\bamenities?\b/.test(lower);
  const wantsProsCons = /\bpros\s*(?:and\s*)?cons?\b|\bproscons\b/.test(lower);
  const imageCountMatch = lower.match(/\b(\d+)\s+(?:hotel\s+)?images?\b/);
  const wantsAllImages = /\ball\s+(?:hotel\s+)?images?\b|\b(?:hotel\s+)?images?\s+all\b/.test(lower);
  const wantsImages =
    !!imageCountMatch ||
    wantsAllImages ||
    (/\bimages?\b/.test(lower) &&
      /\b(?:add|mark|check|include|select|choose|pick)\b/.test(lower)) ||
    /\b(?:hotel\s+)?images?\s+to\s+(?:the\s+)?proposal\b/.test(lower);
  const wantsAllCheckboxes = /\ball\s+checkboxes?\b|\bcheckboxes?\s+all\b/.test(lower);

  if (
    !wantsDetails &&
    !wantsOverview &&
    !wantsReviews &&
    !wantsAmenities &&
    !wantsProsCons &&
    !wantsImages &&
    !wantsAllCheckboxes
  ) {
    return [];
  }

  const actionable =
    /\b(?:add|check|tick|include|mark|choose|pick|uncheck|untick|remove)\b/.test(lower) ||
    /\b(?:to\s+)?(?:the\s+)?proposal\b/.test(lower) ||
    wantsAllCheckboxes ||
    wantsAllImages;
  if (!actionable) {
    return [];
  }

  const checked = !/\b(?:uncheck|untick|remove|hide)\b/.test(lower);
  const source = bareProposalSource(lower, {
    reviews: wantsReviews,
    amenities: wantsAmenities,
    prosCons: wantsProsCons,
  });
  const target = bareProposalTarget(lower);
  const actions: ProposalActionPayload[] = [];
  const base = {
    action: 'source-check' as const,
    source,
    checked,
    hotelName: target.hotelName,
    index: target.index,
  };

  const pickDetails = wantsDetails || (wantsAllCheckboxes && (source === 'info' || source === 'ai' || source === 'tripadvisor'));
  const pickOverview = wantsOverview || (wantsAllCheckboxes && (source === 'info' || source === 'ai'));
  const pickReviews = wantsReviews || (wantsAllCheckboxes && (source === 'google' || source === 'tripadvisor'));
  const pickAmenities = wantsAmenities || (wantsAllCheckboxes && source === 'tripadvisor');
  const pickProsCons = wantsProsCons || (wantsAllCheckboxes && source === 'ai');

  if (pickDetails) {
    actions.push({
      ...base,
      source: source === 'google' ? 'info' : source,
      field: 'details',
    });
  }
  if (pickOverview) {
    actions.push({
      ...base,
      source: source === 'google' || source === 'tripadvisor' ? 'info' : source,
      field: 'overview',
    });
  }
  if (pickReviews) {
    actions.push({ ...base, source: source === 'info' ? 'google' : source, field: 'reviews' });
  }
  if (pickAmenities) {
    actions.push({
      ...base,
      source: source === 'info' ? 'tripadvisor' : source,
      field: 'amenities',
    });
  }
  if (pickProsCons) {
    actions.push({ ...base, source: source === 'info' ? 'ai' : source, field: 'prosCons' });
  }
  if (wantsImages || (wantsAllCheckboxes && (source === 'info' || source === 'google' || source === 'ai'))) {
    const count = imageCountMatch ? Number(imageCountMatch[1]) : wantsAllImages || wantsAllCheckboxes ? 20 : 3;
    actions.push({
      ...base,
      field: 'images',
      value: Number.isFinite(count) && count > 0 ? Math.min(count, 20) : 3,
    });
  }

  return actions;
};

export const resolveBareProposalActions = (text: string): ProposalActionPayload[] => {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  const lower = trimmed.toLowerCase().replace(/\s+/g, ' ');

  const capture = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const match = lower.match(pattern);
      if (match?.[1]) {
        return match[1].trim();
      }
    }
    return null;
  };

  if (
    /\bcurrency\b/.test(lower) ||
    /[€$£฿]/.test(lower) ||
    /\b(?:usd|eur|gbp|thb|dollars?|euros?|pounds?|baht)\b/.test(lower)
  ) {
    const raw =
      capture([
        /\b(?:change|set|switch)\s+(?:the\s+)?currency\s+to\s+([a-z$€£฿]+)\b/,
        /\bcurrency\s+to\s+([a-z$€£฿]+)\b/,
        /\bcurrency\s*[:=]\s*([a-z$€£฿]+)\b/,
        /^currency\s+([a-z$€£฿]+)$/,
        /\b(?:show|display|convert|use|set)\s+(?:prices?\s+)?(?:in\s+|as\s+)?(usd|eur|gbp|thb|dollars?|euros?|pounds?|baht|$|€|£|฿)\b/,
        /\bin\s+(usd|eur|gbp|thb|dollars?|euros?|pounds?|baht)\b/,
        /^(usd|eur|gbp|thb)$/,
      ]) ||
      (lower.match(/[€$£฿]/)?.[0] ?? null) ||
      (lower.match(/\b(usd|eur|gbp|thb|dollars?|euros?|pounds?|baht)\b/)?.[1] ?? null);
    const currency = normalizeCurrency(raw);
    if (currency) {
      return [{ action: 'currency', currency }];
    }
  }

  if (/\bsort(?:ing)?\b/.test(lower)) {
    const raw =
      capture([
        /\b(?:change|set|switch)\s+sort(?:ing)?\s+to\s+(.+)$/,
        /\bsort(?:ing)?\s+(?:by\s+)?(.+)$/,
      ]) || lower;
    const sort = normalizeSort(raw);
    if (sort) {
      return [{ action: 'sort', sort }];
    }
  }

  if (/\btemplate\b/.test(lower)) {
    const raw = capture([
      /\b(?:change|set|switch|use|apply)\s+(?:the\s+)?template\s+(?:to\s+)?([a-z0-9_-]+)\b/,
      /\btemplate\s*[:=]?\s*([a-z0-9_-]+)\b/,
    ]);
    const template = normalizeTemplate(raw);
    if (template) {
      return [{ action: 'template', template }];
    }
  }

  if (
    /^(?:please\s+)?(?:save|update)(?:\s+(?:the\s+)?proposal)?[.!?]?$/.test(lower) ||
    /^(?:please\s+)?save\s+it[.!?]?$/.test(lower)
  ) {
    return [{ action: 'save' }];
  }

  const bareShare = resolveBareShare(lower);
  if (bareShare) {
    return [bareShare];
  }

  const bareComment = resolveBareComment(lower);
  if (bareComment) {
    return [bareComment];
  }

  const actions: ProposalActionPayload[] = [];
  const target = bareProposalTarget(lower);
  const bareCreate = resolveBareCreateProposal(lower);
  if (bareCreate) {
    return [bareCreate];
  }

  const tabLangSource: 'google' | 'tripadvisor' | 'ai' | null = /\b(?:tripadvisor|trip\s*advisor|ta)\b/.test(
    lower,
  )
    ? 'tripadvisor'
    : /\b(?:google|www)\b/.test(lower)
      ? 'google'
      : /\b(?:ai|claude)\b/.test(lower)
        ? 'ai'
        : null;

  const languageToken =
    'en|eng|english|he|heb|hebrew|es|spa|spanish|español|espanol|it|ita|italian|italiano|fr|fra|fre|french|français|francais|ru|rus|russian';

  if (tabLangSource && /\b(?:lang|language)\b/.test(lower)) {
    const raw = capture([
      /\b(?:change|set|switch)\s+(?:the\s+)?(?:ai|claude|google|www|tripadvisor|trip\s*advisor|ta)?\s*(?:hotel\s+info\s+)?(?:lang|language)\s+to\s+([a-z\u0590-\u05FF]+)\b/,
      /\b(?:ai|claude|google|www|tripadvisor|trip\s*advisor|ta)\s+(?:lang|language)\s*(?:to\s+)?([a-z\u0590-\u05FF]+)\b/,
      /\b(?:lang|language)\s*(?:to\s+)?([a-z\u0590-\u05FF]+)\b/,
    ]);
    const language = normalizeLanguage(raw);
    if (language) {
      actions.push({
        action: 'load-source',
        source: tabLangSource,
        language,
        hotelName: target.hotelName,
        index: target.index,
      });
    }
  } else if (
    /\b(?:lang|language)\b/.test(lower) ||
    new RegExp(`\\b(?:show|display|switch|set|change)\\s+in\\s+(?:${languageToken})\\b`).test(lower) ||
    new RegExp(`\\bin\\s+(?:${languageToken})\\b`).test(lower)
  ) {
    const raw = capture([
      /\b(?:change|set|switch)\s+(?:the\s+)?(?:lang|language)\s+to\s+([a-z\u0590-\u05FF]+)\b/,
      /\b(?:lang|language)\s*[:=]?\s*([a-z\u0590-\u05FF]+)\b/,
      /\b(?:show|display|switch|set|change)\s+in\s+([a-z\u0590-\u05FF]+)\b/,
      new RegExp(`\\bin\\s+(${languageToken})\\b`),
    ]);
    const language = normalizeLanguage(raw);
    if (language) {
      actions.push({ action: 'language', language });
    }
  }

  actions.push(...resolveBareSourceChecks(lower));
  return actions;
};

export const resolveBareProposalAction = (text: string): ProposalActionPayload | null =>
  resolveBareProposalActions(text)[0] || null;

export const bareProposalActionCovered = (
  bare: ProposalActionPayload,
  payloads: ProposalActionPayload[],
) =>
  payloads.some((payload) => {
    if (payload.action !== bare.action) {
      return false;
    }
    if (bare.action === 'source-check') {
      if (payload.source !== bare.source || payload.field !== bare.field) {
        return false;
      }
      if (bare.field === 'images' && (bare.value != null || bare.imageIndex != null)) {
        return payload.imageIndex != null || (payload.value != null && payload.value > 0);
      }
      return true;
    }
    if (bare.action === 'load-source') {
      return payload.source === bare.source && payload.language === bare.language;
    }
    if (bare.action === 'share') {
      return (payload.channel || 'email') === (bare.channel || 'email');
    }
    if (bare.action === 'comment') {
      return (payload.mode || 'replace') === (bare.mode || 'replace');
    }
    return true;
  });

export const parseProposalActionPayload = (
  raw: Record<string, unknown> | null | undefined,
): ProposalActionPayload | null => {
  if (!raw || typeof raw.action !== 'string') {
    return null;
  }

  const actionRaw = asString(raw.action)
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, ' ');
  const action = ACTION_ALIASES[actionRaw] || ACTION_ALIASES[actionRaw.replace(/ /g, '-')] || actionRaw;
  if (!(assistantService.PROPOSAL_ACTIONS as readonly string[]).includes(action)) {
    return null;
  }

  const productKey = asString(raw.product).toLowerCase();
  const tab = asString(raw.tab).toLowerCase();
  const priceTypeKey = asString(raw.priceType).toLowerCase().replace(/\s+/g, ' ');
  const channel =
    asString(raw.channel).toLowerCase() ||
    (CHANNELS[actionRaw] ? actionRaw : '') ||
    (action === 'share' ? 'email' : '');
  const sourceKey = asString(raw.source).toLowerCase();
  const field = normalizeCheckboxField(asString(raw.field));
  const part = asString(raw.part).toLowerCase();
  const target = resolveTargetRef(asString(raw.hotelName) || null, asNumber(raw.index));
  const hotelTab = Object.keys(HOTEL_TABS).includes(tab) ? tab : '';
  const source =
    SOURCES[sourceKey] ||
    SOURCE_FROM_TAB[hotelTab] ||
    SOURCE_FROM_TAB[sourceKey] ||
    (field ? sourceFromField(field) : null);
  const mealPlans = Array.isArray(raw.roomMealPlans)
    ? raw.roomMealPlans
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim().toUpperCase())
        .filter((item) => ['BB', 'HB', 'FB', 'AI'].includes(item))
    : null;
  const textValue = typeof raw.text === 'string' ? raw.text : null;
  let applyAll = asBool(raw.applyAll);
  if (applyAll == null) {
    const applyKey = asString(raw.applyAll).toLowerCase().replace(/\s+/g, ' ');
    if (['true', '1', 'yes', 'all', 'update all', 'update-all'].includes(applyKey)) {
      applyAll = true;
    }
    if (['false', '0', 'no', 'update'].includes(applyKey)) {
      applyAll = false;
    }
  }

  let checked = asBool(raw.checked);
  if (actionRaw === 'hide-price' || actionRaw === 'hide-prices') {
    checked = false;
  }
  if (
    actionRaw === 'add-to' ||
    actionRaw === 'add-to-proposal' ||
    actionRaw === 'check' ||
    actionRaw === 'tick' ||
    action === 'select-room' ||
    action === 'source-check'
  ) {
    checked = checked ?? true;
  }
  if (actionRaw === 'uncheck' || actionRaw === 'untick') {
    checked = false;
  }

  let show = asBool(raw.show);
  if (show == null) {
    const showKey = asString(raw.show).toLowerCase();
    if (['true', '1', 'yes', 'all'].includes(showKey)) {
      show = true;
    }
    if (['false', '0', 'no'].includes(showKey)) {
      show = false;
    }
  }
  if (action === 'show-rooms' && show == null) {
    show = true;
  }

  const roomNameRaw =
    asString(raw.roomName) ||
    (action === 'select-room' || action === 'edit-price' ? textValue : null) ||
    null;
  let roomIndexes =
    parseRoomIndexList(raw.roomIndexes) ||
    parseRoomIndexList(raw.indexes) ||
    parseRoomIndexList(raw.roomIndex) ||
    (roomNameRaw ? parseRoomIndexesFromText(roomNameRaw) : null);
  if (
    !roomIndexes?.length &&
    action === 'select-room' &&
    !roomNameRaw &&
    typeof raw.value === 'number' &&
    raw.value >= 1
  ) {
    roomIndexes = Array.from({ length: Math.floor(raw.value) }, (_, index) => index + 1);
  }

  const modeRaw = asString(raw.mode).toLowerCase();
  const mode =
    action === 'comment'
      ? modeRaw === 'append' || modeRaw === 'add'
        ? ('append' as const)
        : modeRaw === 'replace' || modeRaw === 'set' || modeRaw === 'edit'
          ? ('replace' as const)
          : null
      : null;

  return {
    action: action as assistantService.ProposalActionKind,
    tab: PAGE_TABS[tab] || (HOTEL_TABS[tab] !== undefined ? tab : null),
    value: asNumber(raw.value),
    text: textValue,
    mode,
    checked,
    show,
    product: PRODUCT_ALIASES[productKey] || null,
    hotelName: target.hotelName,
    roomName: roomIndexes?.length && roomNameRaw && parseRoomIndexesFromText(roomNameRaw) ? null : roomNameRaw,
    roomIndexes,
    index: target.index,
    priceType:
      PRICE_TYPES[priceTypeKey] ||
      PRICE_TYPES[priceTypeKey.replace(/_/g, '-')] ||
      (action === 'edit-price' ? PRICE_TYPES[(textValue || '').toLowerCase().replace(/\s+/g, ' ')] : null) ||
      null,
    priceValue: asNumber(raw.priceValue) ?? (action === 'edit-price' ? asNumber(raw.value) : null),
    applyAll,
    language: normalizeLanguage(
      asString(raw.language) ||
        asString(raw.lang) ||
        (action === 'language' ? textValue : null) ||
        (action === 'language' && typeof raw.value === 'string' ? raw.value : null) ||
        null,
    ),
    template: normalizeTemplate(
      asString(raw.template) || (action === 'template' ? textValue : null) || null,
    ),
    sort: normalizeSort(asString(raw.sort) || (action === 'sort' ? textValue : null) || null),
    currency: normalizeCurrency(
      asString(raw.currency) ||
        (action === 'currency' ? textValue : null) ||
        (action === 'currency' && typeof raw.value === 'string' ? raw.value : null) ||
        null,
    ),
    channel: CHANNELS[channel] || null,
    email: asString(raw.email) || null,
    ownerName:
      asString(raw.ownerName) ||
      asString(raw.name) ||
      asString(raw.owner) ||
      (action === 'create' || action === 'share' ? textValue : null) ||
      null,
    expireDate: asString(raw.expireDate) || asString(raw.expire) || null,
    password: asString(raw.password) || null,
    cancellationDateDelta: (() => {
      const delta =
        asNumber(raw.cancellationDateDelta) ??
        asNumber(raw.cancellationDelta) ??
        (action === 'create' || action === 'share' ? asNumber(raw.value) : null);
      return delta != null && delta >= 0 && delta <= 5 ? delta : null;
    })(),
    showCancellationDate: (() => {
      const flag =
        asBool(raw.showCancellationDate) ??
        asBool(raw.showCancellation) ??
        asBool(raw.show_cancellation_date);
      if (flag != null) return flag;
      const key = asString(raw.showCancellationDate).toLowerCase();
      if (['true', '1', 'yes', 'show'].includes(key)) return true;
      if (['false', '0', 'no', 'hide'].includes(key)) return false;
      return null;
    })(),
    displayTotalDelta: (() => {
      const flag =
        asBool(raw.displayTotalDelta) ?? asBool(raw.displayTotal) ?? asBool(raw.display_total);
      if (flag != null) return flag;
      const key = asString(raw.displayTotalDelta || raw.displayTotal).toLowerCase();
      if (['true', '1', 'yes', 'show'].includes(key)) return true;
      if (['false', '0', 'no', 'hide'].includes(key)) return false;
      return null;
    })(),
    proposalImage: asString(raw.proposalImage) || asString(raw.imageUrl) || null,
    source,
    field,
    imageIndex: asNumber(raw.imageIndex),
    part: part === 'hotel' || part === 'flight' || part === 'ticket' ? part : null,
    roomFilterName: typeof raw.roomFilterName === 'string' ? raw.roomFilterName : null,
    roomRefundableOnly: asBool(raw.roomRefundableOnly),
    roomAccessibleOnly: asBool(raw.roomAccessibleOnly),
    roomBoardingOnly: asBool(raw.roomBoardingOnly),
    roomMealPlans: mealPlans,
    roomSortByCxl: asBool(raw.roomSortByCxl),
    clearRoomFilters: asBool(raw.clearRoomFilters),
  };
};

const pendingActions: ProposalActionPayload[] = [];
const actionListeners = new Set<() => void>();
let actionVersion = 0;

const notifyActions = () => {
  actionVersion += 1;
  actionListeners.forEach((listener) => listener());
};

export const queueProposalAction = (action: ProposalActionPayload) => {
  pendingActions.push(action);
  notifyActions();
};

export const drainProposalActions = () => pendingActions.splice(0, pendingActions.length);

export const subscribeProposalActions = (listener: () => void) => {
  actionListeners.add(listener);
  return () => {
    actionListeners.delete(listener);
  };
};

export const getProposalActionsVersion = () => actionVersion;

export type ProposalUiIntent =
  | { kind: 'comment'; text: string; mode?: 'replace' | 'append' }
  | { kind: 'clear-comment' }
  | { kind: 'customize'; language?: string; template?: string; sort?: string; currency?: string }
  | {
      kind: 'create';
      ownerName?: string;
      email?: string;
      password?: string;
      expireDate?: string;
      cancellationDateDelta?: number;
      showCancellationDate?: boolean;
      displayTotalDelta?: boolean;
      proposalImage?: string;
      thenShare?: { channel: 'email' | 'pdf' | 'whatsapp' | 'telegram'; email?: string };
    }
  | { kind: 'save' }
  | { kind: 'open-price'; product: string; index: number; roomKey?: string }
  | { kind: 'hotel-tab'; hotelIndex: number; dynamic: boolean; tab: number }
  | { kind: 'show-rooms'; hotelIndex: number; dynamic: boolean; show: boolean }
  | {
      kind: 'filter-rooms';
      hotelIndex: number;
      dynamic: boolean;
      roomFilterName?: string | null;
      roomRefundableOnly?: boolean | null;
      roomAccessibleOnly?: boolean | null;
      roomBoardingOnly?: boolean | null;
      roomMealPlans?: string[] | null;
      roomSortByCxl?: boolean | null;
      clearRoomFilters?: boolean | null;
    }
  | { kind: 'load-source'; hotelIndex: number; dynamic: boolean; source: 'google' | 'tripadvisor'; language: string }
  | { kind: 'claude-lang'; hotelIndex: number; language: string }
  | { kind: 'open-edit'; product: string; index: number; dynamic: boolean; part?: 'hotel' | 'flight' | 'ticket' }
  | { kind: 'share'; channel: 'email' | 'pdf' | 'whatsapp' | 'telegram'; email?: string };

const uiQueue: ProposalUiIntent[] = [];
const uiListeners = new Set<() => void>();

export const enqueueProposalUi = (intents: ProposalUiIntent[]) => {
  if (!intents.length) {
    return;
  }
  uiQueue.push(...intents);
  uiListeners.forEach((listener) => listener());
};

export const consumeProposalUi = (match: (intent: ProposalUiIntent) => boolean) => {
  const taken: ProposalUiIntent[] = [];
  const kept: ProposalUiIntent[] = [];
  uiQueue.forEach((intent) => (match(intent) ? taken : kept).push(intent));
  uiQueue.length = 0;
  uiQueue.push(...kept);
  return taken;
};

export const subscribeProposalUi = (listener: () => void) => {
  uiListeners.add(listener);
  return () => {
    uiListeners.delete(listener);
  };
};

export const useProposalUi = (
  match: (intent: ProposalUiIntent) => boolean,
  onIntent: (intent: ProposalUiIntent) => void,
) => {
  const matchRef = useRef(match);
  const onIntentRef = useRef(onIntent);
  matchRef.current = match;
  onIntentRef.current = onIntent;

  useEffect(() => {
    const run = () => {
      consumeProposalUi((intent) => matchRef.current(intent)).forEach((intent) =>
        onIntentRef.current(intent),
      );
    };
    run();
    return subscribeProposalUi(run);
  }, []);
};

const includesName = (haystack: string, needle: string) => {
  const name = needle.trim().toLowerCase();
  const text = haystack.toLowerCase();
  return text === name || text.includes(name);
};

const hotelNameOf = (hotel: HotProposal) =>
  hotel.item?.name || hotel.item?.item?.name || '';

const findByName = <T>(items: T[], index: number | null | undefined, label: (item: T) => string, name?: string | null) => {
  if (name) {
    const exact = items.findIndex((item) => label(item).toLowerCase() === name.trim().toLowerCase());
    if (exact >= 0) {
      return exact;
    }
    const partial = items.findIndex((item) => includesName(label(item), name));
    if (partial >= 0) {
      return partial;
    }
    if (items.length === 1) {
      return 0;
    }
  }
  if (index && index >= 1 && index <= items.length) {
    return index - 1;
  }
  return items.length === 1 ? 0 : -1;
};

const flightLabel = (flight: FlProposal) => {
  const item = flight.item as any;
  const bounds = [item?.outbound, item?.inbound, item?.returnBound].filter(Boolean);
  const parts: string[] = [];
  bounds.forEach((bound) => {
    (bound.gdsFlyAirs || []).forEach((segment: any) => {
      parts.push(
        segment.airline?.code,
        segment.airline?.name,
        segment.flightNumber,
        segment.departureAirport?.code,
        segment.departureAirport?.cityDesc,
        segment.arrivalAirport?.code,
        segment.arrivalAirport?.cityDesc,
      );
    });
  });
  return parts.filter(Boolean).join(' ');
};

const packageLabel = (pac: ILPacProposal) =>
  `${pac.item?.info?.hotelName || ''} ${pac.item?.deal?.rowName || ''}`;

const transferLabel = (transfer: TransferProposal) =>
  [transfer.forwardItem, transfer.returnItem]
    .flatMap((item) => (item as any)?.transferVehicles || [])
    .map((vehicle: any) => vehicle?.vehicle?.desc)
    .filter(Boolean)
    .join(' ');

const dynamicLabel = (dynamic: DynamicProposal) =>
  [dynamic.hotel?.item?.name, flightLabel({ item: dynamic.flight } as FlProposal)].filter(Boolean).join(' ');

const withTrip = (proposal: Proposal, patch: Partial<TripBuilderConfig>): Proposal => ({
  ...proposal,
  tripBuilder: {
    rows: {},
    ...proposal.tripBuilder,
    ...patch,
  },
});

const nextChecked = (current: boolean | undefined, checked: boolean | null | undefined) =>
  typeof checked === 'boolean' ? checked : !current;

const roomLabel = (room: any) =>
  [
    room?.desc,
    room?.board?.basis?.desc,
    room?.board?.basis?.code,
    ...(room?.hotelRooms || []).map((hotelRoom: any) => hotelRoom.displayName || hotelRoom.desc),
  ]
    .filter(Boolean)
    .join(' ');

const roomIndexesFromPayload = (
  rooms: any[],
  payload: ProposalActionPayload,
  indexMeansRoom: boolean,
): number[] => {
  if (!rooms?.length) {
    return [];
  }

  if (payload.roomIndexes?.length) {
    return payload.roomIndexes.filter((index) => index >= 1 && index <= rooms.length);
  }

  const resolved = resolveTargetRef(payload.roomName || null, null);
  let roomIndex = resolved.index;
  if (!roomIndex && indexMeansRoom && payload.index) {
    roomIndex = payload.index;
  }
  if (roomIndex && roomIndex >= 1 && roomIndex <= rooms.length) {
    return [roomIndex];
  }
  return [];
};

const findRooms = (rooms: any[], payload: ProposalActionPayload, indexMeansRoom = false) => {
  if (!rooms?.length) {
    return [];
  }

  const byIndex = roomIndexesFromPayload(rooms, payload, indexMeansRoom);
  if (byIndex.length) {
    return byIndex.map((index) => rooms[index - 1]).filter(Boolean);
  }

  const resolved = resolveTargetRef(payload.roomName || null, null);
  const name = resolved.hotelName?.trim().toLowerCase();
  if (!name) {
    return rooms.length === 1 ? [rooms[0]] : [];
  }

  const exact = rooms.filter((room) => roomLabel(room).toLowerCase() === name);
  if (exact.length) {
    return exact;
  }
  const partial = rooms.filter((room) => roomLabel(room).toLowerCase().includes(name));
  return partial.length ? [partial[0]] : [];
};

const openRoomsUi = (
  hotelIndex: number,
  dynamic: boolean,
  show = true,
): ProposalUiIntent[] => [
  { kind: 'hotel-tab', hotelIndex, dynamic, tab: 0 },
  { kind: 'show-rooms', hotelIndex, dynamic, show },
];

const toggleId = (ids: string[] | undefined, id: string, checked: boolean | null | undefined) => {
  const list = ids || [];
  const has = list.includes(id);
  const should = typeof checked === 'boolean' ? checked : !has;
  if (should) {
    return has ? list : [...list, id];
  }
  return list.filter((item) => item !== id);
};

const toggleImage = (
  indexes: number[] | undefined,
  imageIndex: number,
  checked: boolean | null | undefined,
  max?: number,
) => {
  const list = indexes || [];
  const has = list.includes(imageIndex);
  const should = typeof checked === 'boolean' ? checked : !has;
  if (!should) {
    return list.filter((item) => item !== imageIndex);
  }
  if (has) {
    return list;
  }
  if (max && list.length >= max) {
    return list;
  }
  return [...list, imageIndex];
};

const resolveImageIndexes = (payload: ProposalActionPayload, availableCount?: number) => {
  if (payload.imageIndex != null && payload.imageIndex >= 0) {
    return [payload.imageIndex];
  }
  let count = typeof payload.value === 'number' && payload.value > 0 ? Math.floor(payload.value) : 0;
  if (!count) {
    if (payload.checked === false) {
      return [];
    }
    count = availableCount && availableCount > 0 ? availableCount : 3;
  }
  const limit =
    availableCount && availableCount > 0 ? Math.min(count, availableCount, 20) : Math.min(count, 20);
  return Array.from({ length: Math.max(limit, 0) }, (_, index) => index);
};

const applyImageIndexes = (
  indexes: number[] | undefined,
  imageIndexes: number[],
  checked: boolean | null | undefined,
  max?: number,
) => {
  let next = indexes || [];
  imageIndexes.forEach((imageIndex) => {
    next = toggleImage(next, imageIndex, checked ?? true, max);
  });
  return next;
};

const mapRoomsPrice = (
  hotel: HotProposal,
  roomKeys: string[] | undefined,
  type: 'p' | 'v' | 'e',
  value: number,
  all: boolean,
) => ({
  ...hotel,
  item: {
    ...hotel.item,
    roomClasses: (hotel.item.roomClasses || []).map((room: any) =>
      all || roomKeys?.includes(room.rcUniqueKey) ? { ...room, viewType: type, viewValue: value } : room,
    ),
  },
});

const setSectionPrice = <T extends { displayPrice: boolean }>(items: T[], checked: boolean | null | undefined) => {
  const next = nextChecked(items[0]?.displayPrice, checked);
  return items.map((item) => ({ ...item, displayPrice: next }));
};

const createOptionsFromPayload = (payload: ProposalActionPayload) => ({
  ...(payload.email?.trim() ? { email: payload.email.trim() } : {}),
  ...(payload.password != null ? { password: payload.password } : {}),
  ...(payload.expireDate?.trim() ? { expireDate: payload.expireDate.trim() } : {}),
  ...(payload.cancellationDateDelta != null
    ? { cancellationDateDelta: payload.cancellationDateDelta }
    : {}),
  ...(typeof payload.showCancellationDate === 'boolean'
    ? { showCancellationDate: payload.showCancellationDate }
    : {}),
  ...(typeof payload.displayTotalDelta === 'boolean'
    ? { displayTotalDelta: payload.displayTotalDelta }
    : {}),
  ...(payload.proposalImage?.trim() ? { proposalImage: payload.proposalImage.trim() } : {}),
});

export interface AppliedProposalAction {
  proposal: Proposal | null;
  ui: ProposalUiIntent[];
  persist: boolean;
  openItemTab: boolean;
}

export const applyProposalDataAction = (
  proposal: Proposal,
  payload: ProposalActionPayload,
): AppliedProposalAction => {
  const ui: ProposalUiIntent[] = [];
  const base = { proposal: null as Proposal | null, ui, persist: false, openItemTab: true };

  switch (payload.action) {
    case 'page-tab':
      return { ...base, openItemTab: false };
    case 'currency':
      if (payload.currency) {
        ui.push({ kind: 'customize', currency: payload.currency });
      }
      return { ...base, ui, openItemTab: false };
    case 'comment': {
      const nextText = joinProposalNotes(
        proposal.existingProposal?.note || '',
        payload.text ?? '',
        payload.mode,
      );
      if (proposal.existingProposal) {
        return {
          ...base,
          ui,
          openItemTab: true,
          proposal: {
            ...proposal,
            existingProposal: {
              ...proposal.existingProposal,
              note: nextText,
            },
          },
        };
      }
      ui.push({
        kind: 'comment',
        text: payload.text ?? '',
        mode: payload.mode === 'append' ? 'append' : 'replace',
      });
      return { ...base, ui, openItemTab: false };
    }
    case 'language':
      if (payload.language) {
        ui.push({ kind: 'customize', language: payload.language });
      }
      return { ...base, ui, openItemTab: false };
    case 'template':
      if (payload.template) {
        ui.push({ kind: 'customize', template: payload.template });
      }
      return { ...base, ui, openItemTab: false };
    case 'sort':
      if (payload.sort) {
        ui.push({ kind: 'customize', sort: payload.sort });
      }
      return { ...base, ui, openItemTab: false };
    case 'create': {
      if (proposal.existingProposal) {
        ui.push({ kind: 'save' });
        return { ...base, ui, openItemTab: true };
      }
      const ownerName = payload.ownerName?.trim() || undefined;
      if (!ownerName) {
        return { ...base, openItemTab: true };
      }
      ui.push({ kind: 'create', ...createOptionsFromPayload(payload), ownerName });
      return { ...base, ui, openItemTab: true };
    }
    case 'save':
      ui.push({ kind: 'save' });
      return { ...base, ui, openItemTab: true };
    case 'share': {
      const channel = payload.channel || 'email';
      const share = {
        channel,
        email: payload.email || undefined,
      } as const;
      if (proposal.existingProposal) {
        ui.push({ kind: 'share', ...share });
        return { ...base, ui, openItemTab: true };
      }
      const ownerName = payload.ownerName?.trim() || undefined;
      if (!ownerName) {
        return { ...base, openItemTab: true };
      }
      ui.push({ kind: 'create', ...createOptionsFromPayload(payload), ownerName, thenShare: share });
      return { ...base, ui, openItemTab: true };
    }
    case 'clear-all':
      ui.push({ kind: 'clear-comment' });
      return {
        ...base,
        ui,
        proposal: {
          hotels: [],
          flights: [],
          dynamic: [],
          ilPackages: [],
          transfers: [],
          existingProposal: undefined,
        },
      };
    case 'cancellation-delta':
      return applyCancellation(proposal, payload, base);
    case 'show-cancellation-date':
      return applyShowCancellation(proposal, payload, base);
    case 'show-price':
      return { ...base, proposal: applyShowPrice(proposal, payload) };
    case 'remove':
      return { ...base, proposal: applyRemove(proposal, payload) };
    case 'edit':
      return applyEdit(proposal, payload, base);
    case 'select-room':
      return applySelectRoom(proposal, payload, base);
    case 'show-rooms':
      return applyShowRooms(proposal, payload, base);
    case 'filter-rooms':
      return applyFilterRooms(proposal, payload, base);
    case 'hotel-tab':
      return applyHotelTab(proposal, payload, base);
    case 'edit-price':
      return applyEditPrice(proposal, payload, base);
    case 'free-text':
      return { ...base, proposal: applyFreeText(proposal, payload) };
    case 'source-check':
      return applySourceCheck(proposal, payload, base);
    case 'load-source':
      return applyLoadSource(proposal, payload, base);
    default:
      return { ...base, openItemTab: false };
  }
};

const hotelIndexFor = (proposal: Proposal, payload: ProposalActionPayload, dynamic: boolean) =>
  dynamic
    ? findByName(proposal.dynamic, payload.index, dynamicLabel, payload.hotelName)
    : findByName(proposal.hotels, payload.index, hotelNameOf, payload.hotelName);

const applyCancellation = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const value = payload.value;
  if (value == null || value < 0 || value > 5) {
    return base;
  }
  const targeted = !!(payload.hotelName || (payload.product === 'hotels' && payload.index));
  const hotelIndex = targeted ? hotelIndexFor(proposal, payload, false) : -1;
  if (targeted && hotelIndex < 0) {
    return base;
  }
  if (hotelIndex >= 0) {
    return {
      ...base,
      proposal: {
        ...proposal,
        hotels: proposal.hotels.map((hotel, index) =>
          index === hotelIndex ? { ...hotel, cancellationDateDelta: value } : hotel,
        ),
      },
    };
  }
  return {
    ...base,
    proposal: withTrip(proposal, { cancellationDateDelta: value }),
  };
};

const applyShowCancellation = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const hotelIndex = payload.hotelName ? hotelIndexFor(proposal, payload, false) : -1;
  if (payload.hotelName && hotelIndex < 0) {
    return base;
  }
  if (hotelIndex >= 0) {
    const checked = nextChecked(proposal.hotels[hotelIndex].showCancellationDate, payload.checked);
    return {
      ...base,
      proposal: {
        ...proposal,
        hotels: proposal.hotels.map((hotel, index) =>
          index === hotelIndex ? { ...hotel, showCancellationDate: checked } : hotel,
        ),
      },
    };
  }
  return {
    ...base,
    proposal: withTrip(proposal, {
      showCancellationDate: nextChecked(proposal.tripBuilder?.showCancellationDate, payload.checked),
    }),
  };
};

const existingProductLabel = (product: any) => {
  const item = product?.listOfItems?.[0];
  const hotelName = item?.hotels?.[0]?.hotel?.name;
  const flightBits = [
    item?.flights?.[0]?.outbound?.gdsFlyAirs?.[0]?.airline?.code,
    item?.flights?.[0]?.outbound?.gdsFlyAirs?.[0]?.flightNumber,
  ]
    .filter(Boolean)
    .join(' ');
  return [hotelName, flightBits, product?.note, String(product?.id || '')].filter(Boolean).join(' ');
};

const findExistingProductIndex = (proposal: Proposal, payload: ProposalActionPayload) => {
  const products = proposal.existingProposal?.listOfProducts || [];
  if (!products.length) {
    return -1;
  }
  return findByName(products, payload.index, existingProductLabel, payload.hotelName);
};

const mapExistingProductsPrice = (
  products: any[],
  type: 'p' | 'v' | 'e',
  value: number,
  onlyIndex: number | null,
) =>
  products.map((product, index) => {
    if (onlyIndex != null && index !== onlyIndex) {
      return product;
    }
    return {
      ...product,
      viewType: type,
      viewValue: value,
      edited: true,
    };
  });

const applyShowPrice = (proposal: Proposal, payload: ProposalActionPayload): Proposal => {
  const product = payload.product;
  const next = { ...proposal };
  if (!product || product === 'hotels') {
    next.hotels = setSectionPrice(proposal.hotels, payload.checked);
  }
  if (!product || product === 'flights') {
    next.flights = setSectionPrice(proposal.flights, payload.checked);
  }
  if (!product || product === 'dynamic') {
    next.dynamic = setSectionPrice(proposal.dynamic, payload.checked);
  }
  if (!product || product === 'transfers') {
    next.transfers = setSectionPrice(proposal.transfers, payload.checked);
  }
  if (!product || product === 'packages') {
    next.ilPackages = setSectionPrice(proposal.ilPackages, payload.checked);
  }
  if (proposal.existingProposal?.listOfProducts?.length) {
    const checked = nextChecked(
      proposal.existingProposal.listOfProducts[0]?.displayPrice,
      payload.checked,
    );
    next.existingProposal = {
      ...proposal.existingProposal,
      listOfProducts: proposal.existingProposal.listOfProducts.map((item) => ({
        ...item,
        displayPrice: checked,
        edited: true,
      })),
    };
  }
  return next;
};

const locateProposalItem = (
  proposal: Proposal,
  payload: ProposalActionPayload,
): { product: assistantService.ProposalActionProduct; index: number } | null => {
  const product = payload.product;
  if (!product || product === 'hotels') {
    const index = findByName(proposal.hotels, payload.index, hotelNameOf, payload.hotelName);
    if (index >= 0) {
      return { product: 'hotels', index };
    }
  }
  if (!product || product === 'flights') {
    const index = findByName(proposal.flights, payload.index, flightLabel, payload.hotelName);
    if (index >= 0 && (product === 'flights' || payload.hotelName)) {
      return { product: 'flights', index };
    }
  }
  if (!product || product === 'dynamic') {
    const index = findByName(proposal.dynamic, payload.index, dynamicLabel, payload.hotelName);
    if (index >= 0 && (product === 'dynamic' || payload.hotelName)) {
      return { product: 'dynamic', index };
    }
  }
  if (!product || product === 'transfers') {
    const index = findByName(proposal.transfers, payload.index, transferLabel, payload.hotelName);
    if (index >= 0 && (product === 'transfers' || payload.hotelName)) {
      return { product: 'transfers', index };
    }
  }
  if (!product || product === 'packages') {
    const index = findByName(proposal.ilPackages, payload.index, packageLabel, payload.hotelName);
    if (index >= 0 && (product === 'packages' || payload.hotelName)) {
      return { product: 'packages', index };
    }
  }
  return null;
};

const applyRemove = (proposal: Proposal, payload: ProposalActionPayload): Proposal | null => {
  const target = locateProposalItem(proposal, payload);
  if (!target && proposal.existingProposal?.listOfProducts?.length) {
    const index = findExistingProductIndex(proposal, payload);
    if (index < 0) {
      return null;
    }
    return {
      ...proposal,
      existingProposal: {
        ...proposal.existingProposal,
        listOfProducts: proposal.existingProposal.listOfProducts.map((item, itemIndex) =>
          itemIndex === index ? { ...item, removed: !item.removed } : item,
        ),
      },
    };
  }
  if (!target) {
    return null;
  }
  if (target.product === 'hotels') {
    return { ...proposal, hotels: proposal.hotels.filter((_, itemIndex) => itemIndex !== target.index) };
  }
  if (target.product === 'flights') {
    return { ...proposal, flights: proposal.flights.filter((_, itemIndex) => itemIndex !== target.index) };
  }
  if (target.product === 'dynamic') {
    return { ...proposal, dynamic: proposal.dynamic.filter((_, itemIndex) => itemIndex !== target.index) };
  }
  if (target.product === 'transfers') {
    return { ...proposal, transfers: proposal.transfers.filter((_, itemIndex) => itemIndex !== target.index) };
  }
  return {
    ...proposal,
    ilPackages: proposal.ilPackages.filter((_, itemIndex) => itemIndex !== target.index),
  };
};

const applyEdit = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const target =
    locateProposalItem(proposal, payload) ||
    locateProposalItem(proposal, { ...payload, product: 'hotels' });

  if (
    !target &&
    proposal.existingProposal?.listOfProducts?.length &&
    !proposal.hotels.length &&
    !proposal.flights.length &&
    !proposal.dynamic.length &&
    !proposal.transfers.length &&
    !proposal.ilPackages.length
  ) {
    const index = findExistingProductIndex(proposal, payload);
    const targetIndex = index >= 0 ? index : 0;
    if (payload.text != null) {
      return {
        ...base,
        proposal: {
          ...proposal,
          existingProposal: {
            ...proposal.existingProposal,
            listOfProducts: proposal.existingProposal.listOfProducts.map((item, itemIndex) =>
              itemIndex === targetIndex
                ? { ...item, note: payload.text ?? '', edited: true }
                : item,
            ),
          },
        },
      };
    }
    return {
      ...base,
      ui: [
        ...base.ui,
        {
          kind: 'open-edit',
          product: 'existing',
          index: targetIndex,
          dynamic: false,
          part: payload.part || 'hotel',
        },
      ],
    };
  }

  if (!target) {
    return base;
  }

  if (payload.text != null) {
    const next = applyFreeText(proposal, {
      ...payload,
      product: target.product,
      index: target.index + 1,
      hotelName: null,
    });
    return next ? { ...base, proposal: next } : base;
  }

  return {
    ...base,
    ui: [
      ...base.ui,
      {
        kind: 'open-edit',
        product: target.product === 'packages' ? 'ilPackages' : target.product,
        index: target.index,
        dynamic: target.product === 'dynamic',
        part: payload.part || 'hotel',
      },
    ],
  };
};

const hasRoomPositionTarget = (payload: ProposalActionPayload) =>
  !!payload.roomIndexes?.length ||
  resolveTargetRef(payload.roomName || null, null).index != null ||
  !!(payload.roomName && parseRoomIndexesFromText(payload.roomName));

const hotelPayloadForRoomAction = (payload: ProposalActionPayload): ProposalActionPayload => {
  if (payload.hotelName || hasRoomPositionTarget(payload)) {
    return { ...payload, index: null };
  }
  return payload;
};

const applySelectRoom = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const checked = payload.checked ?? true;
  const indexMeansRoom = !!payload.hotelName || hasRoomPositionTarget(payload);
  if (payload.product === 'dynamic') {
    const index = hotelIndexFor(proposal, hotelPayloadForRoomAction(payload), true);
    const dynamic = proposal.dynamic[index];
    const rooms = findRooms(dynamic?.hotel?.roomClasses || [], payload, indexMeansRoom || !!payload.hotelName);
    if (!dynamic?.hotel || !rooms.length || index < 0) {
      return base;
    }
    let choosenRoomIds = dynamic.hotel.choosenRoomIds || [];
    rooms.forEach((room) => {
      choosenRoomIds = toggleId(choosenRoomIds, room.rcUniqueKey, checked);
    });
    return {
      ...base,
      ui: [...base.ui, ...openRoomsUi(index, true)],
      proposal: {
        ...proposal,
        dynamic: proposal.dynamic.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                hotel: {
                  ...item.hotel!,
                  choosenRoomIds,
                },
              }
            : item,
        ),
      },
    };
  }

  const index = hotelIndexFor(proposal, hotelPayloadForRoomAction(payload), false);
  const hotel = proposal.hotels[index];
  const rooms = findRooms(
    hotel?.item?.roomClasses || [],
    payload,
    indexMeansRoom || !!payload.hotelName || proposal.hotels.length === 1,
  );
  if (!hotel || !rooms.length || index < 0) {
    return base;
  }
  let choosenRoomIds = hotel.item.choosenRoomIds || [];
  rooms.forEach((room) => {
    choosenRoomIds = toggleId(choosenRoomIds, room.rcUniqueKey, checked);
  });
  return {
    ...base,
    ui: [...base.ui, ...openRoomsUi(index, false)],
    proposal: {
      ...proposal,
      hotels: proposal.hotels.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              item: {
                ...item.item,
                choosenRoomIds,
              },
            }
          : item,
      ),
    },
  };
};

const applyShowRooms = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const dynamic = payload.product === 'dynamic';
  const index = hotelIndexFor(proposal, hotelPayloadForRoomAction(payload), dynamic);
  if (index < 0) {
    return base;
  }
  return {
    ...base,
    ui: [
      ...base.ui,
      ...openRoomsUi(index, dynamic, typeof payload.show === 'boolean' ? payload.show : true),
    ],
  };
};

const applyFilterRooms = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const dynamic = payload.product === 'dynamic';
  const index = hotelIndexFor(proposal, hotelPayloadForRoomAction(payload), dynamic);
  if (index < 0) {
    return base;
  }
  return {
    ...base,
    ui: [
      ...base.ui,
      ...openRoomsUi(index, dynamic, true),
      {
        kind: 'filter-rooms',
        hotelIndex: index,
        dynamic,
        roomFilterName: payload.roomFilterName,
        roomRefundableOnly: payload.roomRefundableOnly,
        roomAccessibleOnly: payload.roomAccessibleOnly,
        roomBoardingOnly: payload.roomBoardingOnly,
        roomMealPlans: payload.roomMealPlans,
        roomSortByCxl: payload.roomSortByCxl,
        clearRoomFilters: payload.clearRoomFilters,
      },
    ],
  };
};

const applyHotelTab = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const dynamic = payload.product === 'dynamic';
  const index = hotelIndexFor(proposal, payload, dynamic);
  const tab = HOTEL_TABS[(payload.tab || '').toLowerCase()];
  if (index < 0 || tab === undefined) {
    return base;
  }
  return {
    ...base,
    ui: [...base.ui, { kind: 'hotel-tab', hotelIndex: index, dynamic, tab }],
  };
};

const resolveHotelRoomKeys = (hotel: HotProposal, payload: ProposalActionPayload) => {
  const rooms = hotel.item.roomClasses || [];
  if (!rooms.length) {
    return [] as string[];
  }
  const named = findRooms(rooms, payload, hasRoomPositionTarget(payload));
  if (named.length) {
    return named.map((room) => room.rcUniqueKey as string).filter(Boolean);
  }
  const chosenIds = hotel.item.choosenRoomIds || [];
  if (chosenIds.length) {
    return chosenIds.filter((id: string) => rooms.some((room: any) => room.rcUniqueKey === id));
  }
  return rooms[0]?.rcUniqueKey ? [rooms[0].rcUniqueKey as string] : [];
};

const openPriceUi = (
  product: string,
  index: number,
  roomKey?: string,
): ProposalUiIntent[] => {
  const ui: ProposalUiIntent[] = [];
  if (product === 'hotels') {
    ui.push(...openRoomsUi(index, false, true));
  }
  ui.push({ kind: 'open-price', product, index, roomKey });
  return ui;
};

const applyEditPrice = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const type = payload.priceType || 'v';
  const value = payload.priceValue;
  const applyAll = !!payload.applyAll && type !== 'e';

  if (value == null) {
    if (
      proposal.existingProposal?.listOfProducts?.length &&
      !proposal.hotels.length &&
      !proposal.flights.length &&
      !proposal.dynamic.length &&
      !proposal.transfers.length &&
      !proposal.ilPackages.length
    ) {
      const index = findExistingProductIndex(proposal, payload);
      const targetIndex = index >= 0 ? index : 0;
      return {
        ...base,
        ui: [...base.ui, { kind: 'open-price', product: 'existing', index: targetIndex }],
      };
    }
    const located = locatePriceTarget(proposal, payload);
    if (!located) {
      return base;
    }
    if (located.product === 'hotels' && !located.roomKeys.length) {
      return base;
    }
    return {
      ...base,
      ui: [
        ...base.ui,
        ...openPriceUi(located.product, located.index, located.roomKeys[0]),
      ],
    };
  }

  if (applyAll) {
    const existingProducts = proposal.existingProposal?.listOfProducts;
    return {
      ...base,
      proposal: {
        ...proposal,
        dynamic: proposal.dynamic.map((item) => ({ ...item, viewType: type, viewValue: value })),
        flights: proposal.flights.map((item) => ({ ...item, viewType: type, viewValue: value })),
        transfers: proposal.transfers.map((item) => ({ ...item, viewType: type, viewValue: value })),
        ilPackages: proposal.ilPackages.map((item) => ({ ...item, viewType: type, viewValue: value })),
        hotels: proposal.hotels.map((hotel) => mapRoomsPrice(hotel, undefined, type, value, true)),
        existingProposal: proposal.existingProposal
          ? {
              ...proposal.existingProposal,
              listOfProducts: mapExistingProductsPrice(existingProducts || [], type, value, null),
            }
          : proposal.existingProposal,
      },
    };
  }

  if (
    proposal.existingProposal?.listOfProducts?.length &&
    !proposal.hotels.length &&
    !proposal.flights.length &&
    !proposal.dynamic.length &&
    !proposal.transfers.length &&
    !proposal.ilPackages.length
  ) {
    const index = findExistingProductIndex(proposal, payload);
    if (index < 0 && proposal.existingProposal.listOfProducts.length !== 1) {
      return base;
    }
    const targetIndex = index >= 0 ? index : 0;
    return {
      ...base,
      proposal: {
        ...proposal,
        existingProposal: {
          ...proposal.existingProposal,
          listOfProducts: mapExistingProductsPrice(
            proposal.existingProposal.listOfProducts,
            type,
            value,
            targetIndex,
          ),
        },
      },
    };
  }

  const located = locatePriceTarget(proposal, payload);
  if (!located) {
    return base;
  }

  if (located.product === 'hotels') {
    if (!located.roomKeys.length) {
      return base;
    }
    return {
      ...base,
      proposal: {
        ...proposal,
        hotels: proposal.hotels.map((hotel, index) =>
          index === located.index ? mapRoomsPrice(hotel, located.roomKeys, type, value, false) : hotel,
        ),
      },
    };
  }

  if (located.product === 'flights') {
    return {
      ...base,
      proposal: {
        ...proposal,
        flights: proposal.flights.map((item, index) =>
          index === located.index ? { ...item, viewType: type, viewValue: value } : item,
        ),
      },
    };
  }

  if (located.product === 'dynamic') {
    return {
      ...base,
      proposal: {
        ...proposal,
        dynamic: proposal.dynamic.map((item, index) =>
          index === located.index ? { ...item, viewType: type, viewValue: value } : item,
        ),
      },
    };
  }

  if (located.product === 'transfers') {
    return {
      ...base,
      proposal: {
        ...proposal,
        transfers: proposal.transfers.map((item, index) =>
          index === located.index ? { ...item, viewType: type, viewValue: value } : item,
        ),
      },
    };
  }

  return {
    ...base,
    proposal: {
      ...proposal,
      ilPackages: proposal.ilPackages.map((item, index) =>
        index === located.index ? { ...item, viewType: type, viewValue: value } : item,
      ),
    },
  };
};

const locatePriceTarget = (proposal: Proposal, payload: ProposalActionPayload) => {
  const product = payload.product || (proposal.hotels.length ? 'hotels' : null);
  if (product === 'hotels' || !product) {
    const index = hotelIndexFor(proposal, hotelPayloadForRoomAction(payload), false);
    const hotel = index >= 0 ? proposal.hotels[index] : undefined;
    if (hotel) {
      return {
        product: 'hotels' as const,
        index,
        roomKeys: resolveHotelRoomKeys(hotel, payload),
      };
    }
  }
  if (product === 'flights') {
    const index = findByName(proposal.flights, payload.index, flightLabel, payload.hotelName);
    return index >= 0 ? { product: 'flights' as const, index, roomKeys: [] as string[] } : null;
  }
  if (product === 'dynamic') {
    const index = findByName(proposal.dynamic, payload.index, dynamicLabel, payload.hotelName);
    return index >= 0 ? { product: 'dynamic' as const, index, roomKeys: [] as string[] } : null;
  }
  if (product === 'transfers') {
    const index = findByName(proposal.transfers, payload.index, transferLabel, payload.hotelName);
    return index >= 0 ? { product: 'transfers' as const, index, roomKeys: [] as string[] } : null;
  }
  if (product === 'packages') {
    const index = findByName(proposal.ilPackages, payload.index, packageLabel, payload.hotelName);
    return index >= 0 ? { product: 'ilPackages' as const, index, roomKeys: [] as string[] } : null;
  }
  return null;
};

const applyFreeText = (proposal: Proposal, payload: ProposalActionPayload): Proposal | null => {
  const text = payload.text ?? '';
  const order = payload.value ?? undefined;
  if (payload.product === 'flights') {
    const index = findByName(proposal.flights, payload.index, flightLabel, payload.hotelName);
    if (index < 0) return null;
    return {
      ...proposal,
      flights: proposal.flights.map((item, itemIndex) =>
        itemIndex === index ? { ...item, freeText: text, displayOrder: order } : item,
      ),
    };
  }
  if (payload.product === 'transfers') {
    const index = findByName(proposal.transfers, payload.index, transferLabel, payload.hotelName);
    if (index < 0) return null;
    return {
      ...proposal,
      transfers: proposal.transfers.map((item, itemIndex) =>
        itemIndex === index ? { ...item, freeText: text, displayOrder: order } : item,
      ),
    };
  }
  if (payload.product === 'packages') {
    const index = findByName(proposal.ilPackages, payload.index, packageLabel, payload.hotelName);
    if (index < 0) return null;
    return {
      ...proposal,
      ilPackages: proposal.ilPackages.map((item, itemIndex) =>
        itemIndex === index ? { ...item, freeText: text, displayOrder: order } : item,
      ),
    };
  }
  if (payload.product === 'dynamic') {
    const index = findByName(proposal.dynamic, payload.index, dynamicLabel, payload.hotelName);
    if (index < 0) return null;
    const part = payload.part || 'hotel';
    const field = part === 'flight' ? 'freeTextFlight' : part === 'ticket' ? 'freeTextTicket' : 'freeTextHotel';
    return {
      ...proposal,
      dynamic: proposal.dynamic.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: text, displayOrder: order } : item,
      ),
    };
  }
  const index = hotelIndexFor(proposal, payload, false);
  if (index < 0) return null;
  return {
    ...proposal,
    hotels: proposal.hotels.map((item, itemIndex) =>
      itemIndex === index ? { ...item, freeText: text, displayOrder: order } : item,
    ),
  };
};

const sourceTab = (source: string) =>
  source === 'google' ? 3 : source === 'tripadvisor' ? 2 : source === 'ai' ? 4 : 1;

const applySourceCheck = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const dynamic = payload.product === 'dynamic';
  const field = payload.field;
  const source =
    payload.source ||
    (payload.tab ? SOURCE_FROM_TAB[payload.tab] : null) ||
    (field ? sourceFromField(field) : null);
  if (!source || !field) {
    return base;
  }
  const index = hotelIndexFor(
    proposal,
    {
      ...payload,
      index: payload.index ?? (payload.hotelName ? null : proposal.hotels.length === 1 ? 1 : null),
    },
    dynamic && (source === 'google' || source === 'tripadvisor'),
  );
  if (index < 0 || (dynamic && source !== 'google' && source !== 'tripadvisor')) {
    return base;
  }

  const ui = [
    ...base.ui,
    { kind: 'hotel-tab' as const, hotelIndex: index, dynamic, tab: sourceTab(source) },
  ];

  if (dynamic) {
    return {
      ...base,
      ui,
      persist: true,
      proposal: {
        ...proposal,
        dynamic: proposal.dynamic.map((item, itemIndex) =>
          itemIndex === index ? patchSource(item, source, field, payload) : item,
        ),
      },
    };
  }

  const hotel = proposal.hotels[index];
  const patched = patchSource(hotel, source, field, payload);
  if (source === 'ai' && payload.language) {
    ui.push({ kind: 'claude-lang', hotelIndex: index, language: patched.claudeHotelInfoConfig?.language || payload.language });
  }
  return {
    ...base,
    ui,
    persist: true,
    proposal: {
      ...proposal,
      hotels: proposal.hotels.map((item, itemIndex) => (itemIndex === index ? patched : item)),
    },
  };
};

const patchSource = <T extends HotProposal | DynamicProposal>(
  item: T,
  source: 'google' | 'tripadvisor' | 'info' | 'ai',
  field: NonNullable<ProposalActionPayload['field']>,
  payload: ProposalActionPayload,
): T => {
  if (source === 'google' || source === 'tripadvisor') {
    const key = source === 'google' ? 'googleChecked' : 'tripAdvisorChecked';
    const current = ((item as any)[key] || {}) as Record<string, unknown>;
    if (field === 'images') {
      const available =
        source === 'google'
          ? (item as any).googleData?.serpResults?.serpHotel?.images?.length
          : undefined;
      const imageIndexes = resolveImageIndexes(
        payload,
        typeof available === 'number' ? available : undefined,
      );
      if (!imageIndexes.length) {
        return item;
      }
      return {
        ...item,
        [key]: {
          ...current,
          imagesIndexes: applyImageIndexes(
            current.imagesIndexes as number[] | undefined,
            imageIndexes,
            payload.checked,
          ),
        },
      };
    }
    return {
      ...item,
      [key]: {
        ...current,
        [field]: nextChecked(current[field] as boolean | undefined, payload.checked),
      },
    };
  }

  if (!('item' in item)) {
    return item;
  }

  if (source === 'info') {
    const current: HotelInfoConfig = item.hotelInfoConfig || {
      language: payload.language || 'en',
      imagesIndexes: [],
    };
    const available = Array.isArray((item as any).hotelInformation?.images)
      ? (item as any).hotelInformation.images.length
      : undefined;
    return {
      ...item,
      hotelInfoConfig: patchInfoConfig(current, field, payload, 3, available),
    };
  }

  const current: ClaudeHotelInfoConfig = item.claudeHotelInfoConfig || {
    language: CLAUDE_LANGUAGE[payload.language || 'en'] || 'English',
    imagesIndexes: [],
  };
  return { ...item, claudeHotelInfoConfig: patchInfoConfig(current, field, payload, 3) };
};

const patchInfoConfig = <T extends { imagesIndexes: number[]; language: string }>(
  current: T,
  field: NonNullable<ProposalActionPayload['field']>,
  payload: ProposalActionPayload,
  max: number,
  availableCount?: number,
): T => {
  if (field === 'images') {
    const imageIndexes = resolveImageIndexes(payload, availableCount);
    if (!imageIndexes.length) {
      return current;
    }
    return {
      ...current,
      imagesIndexes: applyImageIndexes(current.imagesIndexes, imageIndexes, payload.checked, max),
    };
  }
  return {
    ...current,
    [field]: nextChecked((current as any)[field], payload.checked),
  };
};

const applyLoadSource = (
  proposal: Proposal,
  payload: ProposalActionPayload,
  base: AppliedProposalAction,
): AppliedProposalAction => {
  const source = payload.source;
  const language = payload.language || 'en';
  if (!source) {
    return base;
  }
  const dynamic = payload.product === 'dynamic';
  const index = hotelIndexFor(proposal, payload, dynamic);
  if (index < 0) {
    return base;
  }

  const ui: ProposalUiIntent[] = [
    ...base.ui,
    { kind: 'hotel-tab', hotelIndex: index, dynamic, tab: sourceTab(source) },
  ];

  if (source === 'google' || source === 'tripadvisor') {
    ui.push({ kind: 'load-source', hotelIndex: index, dynamic, source, language });
    return { ...base, ui };
  }

  if (dynamic) {
    return { ...base, ui };
  }

  if (source === 'ai') {
    const languageName = CLAUDE_LANGUAGE[language] || language;
    ui.push({ kind: 'claude-lang', hotelIndex: index, language: languageName });
    return {
      ...base,
      ui,
      persist: true,
      proposal: {
        ...proposal,
        hotels: proposal.hotels.map((hotel, itemIndex) =>
          itemIndex === index
            ? {
                ...hotel,
                claudeHotelInfoConfig: {
                  imagesIndexes: hotel.claudeHotelInfoConfig?.imagesIndexes || [],
                  ...hotel.claudeHotelInfoConfig,
                  language: languageName,
                },
              }
            : hotel,
        ),
      },
    };
  }

  return {
    ...base,
    ui,
    persist: true,
    proposal: {
      ...proposal,
      hotels: proposal.hotels.map((hotel, itemIndex) =>
        itemIndex === index
          ? {
              ...hotel,
              hotelInfoConfig: {
                ...hotel.hotelInfoConfig,
                imagesIndexes: [],
                details: undefined,
                overview: undefined,
                language,
              },
            }
          : hotel,
      ),
    },
  };
};
